import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useContext } from "react"
import { CanvasControlsOverlay } from "@/components/canvas-controls/CanvasControls"
import { CanvasControlsContext } from "@/components/canvas-controls/CanvasControlsContext"
import {
	hoveredPeopleFaceIndexAtom,
	peopleFaceBoxesAtom,
	selectedPeopleFaceIndexAtom,
	visiblePeopleFaceIndexesAtom,
} from "@/store/editor/people-face-boxes"
import { showingMaskingToolsOverlayAtom } from "@/store/editor/masks-state"
import { showingPeoplePanelAtom } from "@/store/ui-views-state"

export const PeopleFaceBoxesOverlay = () => {
	const showingPeoplePanel = useAtomValue(showingPeoplePanelAtom)
	const pointerOverEditor = useAtomValue(showingMaskingToolsOverlayAtom)
	const faceBoxes = useAtomValue(peopleFaceBoxesAtom)
	const visiblePeopleFaceIndexes = useAtomValue(visiblePeopleFaceIndexesAtom)
	const hoveredPersonIndex = useAtomValue(hoveredPeopleFaceIndexAtom)
	const [selectedPersonIndex, setSelectedPersonIndex] = useAtom(selectedPeopleFaceIndexAtom)
	const setHoveredPersonIndex = useSetAtom(hoveredPeopleFaceIndexAtom)
	const shouldShowFaceBoxes = pointerOverEditor || hoveredPersonIndex !== null
	const visiblePeopleFaceIndexSet = new Set(visiblePeopleFaceIndexes)
	const visibleFaceBoxes = faceBoxes.filter(
		(box) =>
			visiblePeopleFaceIndexSet.has(box.personIndex) &&
			(pointerOverEditor || box.personIndex === hoveredPersonIndex),
	)

	if (!showingPeoplePanel || visibleFaceBoxes.length === 0 || !shouldShowFaceBoxes) {
		return null
	}

	return (
		<CanvasControlsOverlay style={{ pointerEvents: "none" }}>
			{visibleFaceBoxes.map((box) => (
				<FaceBoundingBox
					key={box.personIndex}
					personIndex={box.personIndex}
					rect={box.rect}
					active={selectedPersonIndex === box.personIndex}
					onSelect={setSelectedPersonIndex}
					onHover={setHoveredPersonIndex}
				/>
			))}
		</CanvasControlsOverlay>
	)
}

const FaceBoundingBox = (props: {
	personIndex: number
	rect: { x: number; y: number; width: number; height: number }
	active: boolean
	onSelect: (personIndex: number) => void
	onHover: (personIndex: number | null) => void
}) => {
	const { parentWidth, parentHeight } = useContext(CanvasControlsContext)
	const x = props.rect.x * parentWidth
	const y = props.rect.y * parentHeight
	const width = props.rect.width * parentWidth
	const height = props.rect.height * parentHeight
	const cornerLength = Math.min(width, height) * 0.28
	const color = props.active ? "#00c8a4" : "#f8fafc"

	return (
		<g
			className="pointer-events-auto cursor-pointer"
			style={{ pointerEvents: "auto" }}
			onClick={() => props.onSelect(props.personIndex)}
			onPointerEnter={() => props.onHover(props.personIndex)}
			onPointerLeave={() => props.onHover(null)}
			onPointerDown={(event) => {
				event.stopPropagation()
			}}
		>
			<rect x={x} y={y} width={width} height={height} fill="transparent" />
			<g
				stroke={color}
				strokeWidth={2}
				strokeLinecap="square"
				vectorEffect="non-scaling-stroke"
				filter="drop-shadow(0 1px 1px rgb(0 0 0 / 0.55))"
			>
				<line x1={x} y1={y} x2={x + cornerLength} y2={y} />
				<line x1={x} y1={y} x2={x} y2={y + cornerLength} />
				<line x1={x + width} y1={y} x2={x + width - cornerLength} y2={y} />
				<line x1={x + width} y1={y} x2={x + width} y2={y + cornerLength} />
				<line x1={x} y1={y + height} x2={x + cornerLength} y2={y + height} />
				<line x1={x} y1={y + height} x2={x} y2={y + height - cornerLength} />
				<line
					x1={x + width}
					y1={y + height}
					x2={x + width - cornerLength}
					y2={y + height}
				/>
				<line
					x1={x + width}
					y1={y + height}
					x2={x + width}
					y2={y + height - cornerLength}
				/>
			</g>
		</g>
	)
}
